// Team ▸ Pod filter semantics. A pod belongs to exactly one team, so the
// Team chip is one two-level checklist rather than two independent chips —
// that keeps "Team: Lennar.com + Pod: ISC Experience" (an empty roadmap with
// no explanation) impossible to express.
//
// Selection model:
//   - team selected  → every initiative on that team, including ones with no
//                      pod assigned. Its pods read as implied.
//   - pod selected   → only that pod. Its team row shows a partial mark.
//   - team + pod     → OR across both lists (an initiative matches if its team
//                      is selected or its pod is selected).
// All functions are pure so the chip, the URL mirror, and the predicate agree.

import { TEAMS } from "./constants";
import type { Team } from "./types";

export type TeamPodSelection = { team: string[]; pod: string[] };

export function teamOfPod(podId: string, teams: Team[] = TEAMS): Team | undefined {
  return teams.find(t => t.pods.some(p => p.id === podId));
}

/** True when the initiative passes the team/pod filter (empty selection passes everything). */
export function matchesTeamFilter(
  sel: TeamPodSelection,
  init: { teamId: string; podId?: string | null },
): boolean {
  if (!sel.team.length && !sel.pod.length) return true;
  if (sel.team.includes(init.teamId)) return true;
  return Boolean(init.podId) && sel.pod.includes(init.podId as string);
}

/**
 * Click on a team row. Selecting a team absorbs any of its pods that were
 * individually checked (they're implied now); deselecting simply removes it.
 */
export function toggleTeam(sel: TeamPodSelection, teamId: string, teams: Team[] = TEAMS): TeamPodSelection {
  if (sel.team.includes(teamId)) {
    return { team: sel.team.filter(t => t !== teamId), pod: sel.pod };
  }
  const team = teams.find(t => t.id === teamId);
  const ownPods = new Set((team?.pods ?? []).map(p => p.id));
  return {
    team: [...sel.team, teamId],
    pod: sel.pod.filter(p => !ownPods.has(p)),
  };
}

/**
 * Click on a pod row. If its team is selected as a whole, this narrows to
 * "every other pod on that team" in one click (standard tree-checkbox
 * behaviour). Otherwise it's a plain toggle.
 */
export function togglePod(sel: TeamPodSelection, podId: string, teams: Team[] = TEAMS): TeamPodSelection {
  const team = teamOfPod(podId, teams);
  if (team && sel.team.includes(team.id)) {
    return {
      team: sel.team.filter(t => t !== team.id),
      pod: [...sel.pod, ...team.pods.map(p => p.id).filter(p => p !== podId && !sel.pod.includes(p))],
    };
  }
  return {
    team: sel.team,
    pod: sel.pod.includes(podId) ? sel.pod.filter(p => p !== podId) : [...sel.pod, podId],
  };
}

export type TeamRowState = "on" | "partial" | "off";

/** Visual state of a team row given the selection. */
export function teamRowState(sel: TeamPodSelection, teamId: string, teams: Team[] = TEAMS): TeamRowState {
  if (sel.team.includes(teamId)) return "on";
  const team = teams.find(t => t.id === teamId);
  return team && team.pods.some(p => sel.pod.includes(p.id)) ? "partial" : "off";
}

/** True when the pod is explicitly checked or implied by its team. */
export function podIsOn(sel: TeamPodSelection, podId: string, teams: Team[] = TEAMS): boolean {
  if (sel.pod.includes(podId)) return true;
  const team = teamOfPod(podId, teams);
  return Boolean(team && sel.team.includes(team.id));
}

/**
 * Chip label. Single team or single pod reads by name; several pods on one
 * team read as "N pods"; anything mixed reads as "N selected".
 */
export function teamFilterLabel(sel: TeamPodSelection, base = "Team", teams: Team[] = TEAMS): string {
  const nTeams = sel.team.length;
  const nPods = sel.pod.length;
  if (!nTeams && !nPods) return base;
  if (nTeams === 1 && !nPods) {
    return `${base}: ${teams.find(t => t.id === sel.team[0])?.name ?? sel.team[0]}`;
  }
  if (!nTeams && nPods === 1) {
    const pod = teams.flatMap(t => t.pods).find(p => p.id === sel.pod[0]);
    return `${base}: ${pod?.name ?? sel.pod[0]}`;
  }
  if (!nTeams) {
    const owners = new Set(sel.pod.map(p => teamOfPod(p, teams)?.id));
    if (owners.size === 1) return `${base}: ${nPods} pods`;
  }
  return `${base}: ${nTeams + nPods} selected`;
}
